import React from 'react';
import { Drawer, Table, Tabs, Button, Upload, Input, Select } from 'antd';
import { Upload as UploadIcon, FileSpreadsheet, TriangleAlert } from 'lucide-react';
import { ModernStores, TraditionalStores } from '../../common';
import { useCreateStoreDrawer } from './createStoreDrawer.controller';
import { searchableSelect } from '../../../utils/selectSearch';

/** Label above a control, with a required mark. */
function Field({ label, required, children }) {
    return (
        <label className="flex flex-col gap-1.5 min-w-0">
            <span className="text-[13px] font-medium text-surface-700">
                {label} {required ? <span className="text-danger-fg">*</span> : ''}
            </span>
            {children}
        </label>
    );
}

/** Numbered step of the bulk upload flow. */
function Step({ title, children }) {
    return (
        <div className="flex flex-col gap-2.5">
            <p className="m-0 text-sm font-medium text-surface-700">{title}</p>
            {children}
        </div>
    );
}

export default function CreateStoreDrawer({ clients, open, setOpen, createStore, createMultipleStores, contextHolder, disabled, isEdit, store, setStore }) {
    const {
        name,
        setName,
        rif,
        setRif,
        client,
        filteredClients,
        channel,
        storeType,
        setStoreType,
        hasWarehouse,
        setHasWarehouse,
        stores,
        setMassiveClient,
        channelClients,
        handleChannel,
        handleClient,
        onCloseDrawer,
        downloadExcel,
        LoadExcelData
    } = useCreateStoreDrawer({ clients, setOpen, isEdit, store, setStore });

    const tabs = [
        {
            key: '1',
            label: 'Carga Individual',
            children: (
                <div className="flex flex-col gap-5 pt-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Nombre del Piso de Venta" required>
                            <Input
                                placeholder="Ingresa el nombre del piso de venta..."
                                value={name}
                                allowClear
                                onChange={(e) => setName(e.target.value)}
                            />
                        </Field>
                        <Field label="Rif del Piso de Venta" required>
                            <Input
                                prefix={<span className="text-surface-400">J-</span>}
                                placeholder="Ingresa el rif del piso de venta..."
                                value={rif}
                                allowClear
                                onChange={(e) => setRif(e.target.value)}
                            />
                        </Field>
                        <Field label="Selecciona el canal" required>
                            <Select
                                className="w-full"
                                value={channel}
                                options={[
                                    { value: 'moderno', label: 'Canal Moderno' },
                                    { value: 'tradicional', label: 'Canal Tradicional' }
                                ]}
                                onChange={(e) => handleChannel(e)}
                            />
                        </Field>
                        <Field label="Selecciona el cliente" required>
                            <Select
                                className="w-full"
                                {...searchableSelect}
                                placeholder="Busca por nombre"
                                value={client}
                                options={channelClients?.map((client) => ({
                                    label: client.name ?? '',
                                    value: client._id ?? ''
                                }))}
                                onChange={(e) => handleClient(e)}
                            />
                        </Field>
                        <Field label="Selecciona el tipo de PDV" required>
                            <Select
                                className="w-full"
                                {...searchableSelect}
                                value={storeType}
                                onChange={(e) => setStoreType(e)}
                                options={channel === 'moderno' ? ModernStores : TraditionalStores}
                            />
                        </Field>
                        <Field label="El PDV tiene almacén?" required>
                            <Select
                                className="w-full"
                                value={hasWarehouse}
                                options={[
                                    { label: 'Si', value: true },
                                    { label: 'No', value: false }
                                ]}
                                onChange={(e) => setHasWarehouse(e)}
                            />
                        </Field>
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t border-surface-100">
                        <Button onClick={onCloseDrawer}>Cancelar</Button>
                        <Button
                            type="primary"
                            disabled={disabled}
                            onClick={() => createStore({ name, rif, storeType, client, filteredClients, channel, hasWarehouse, isEdit })}>
                            {isEdit ? 'Actualizar Piso de Venta' : 'Crear Piso de Venta'}
                        </Button>
                    </div>
                </div>
            )
        },
        {
            key: '2',
            label: 'Carga Masiva',
            children: (
                <div className="flex flex-col gap-6 pt-2">
                    <div className="flex items-start gap-2.5 rounded-lg border border-warning-border bg-warning-bg px-3.5 py-3 text-sm text-warning-fg">
                        <TriangleAlert className="mt-0.5" />
                        <p className="m-0">NOTA: La carga masiva es por cliente, todos los pisos deben ser del mismo cliente</p>
                    </div>
                    <Step title="1. Descarga el excel y llena los datos">
                        <div>
                            <Button icon={<FileSpreadsheet />} onClick={() => downloadExcel()}>
                                Descargar Excel
                            </Button>
                        </div>
                    </Step>
                    <Step title="2. Selecciona el cliente">
                        <Field label="Selecciona el cliente" required>
                            <Select
                                className="w-full"
                                {...searchableSelect}
                                placeholder="Busca por nombre"
                                options={clients?.map((client) => ({
                                    label: client.name ?? '',
                                    value: client._id ?? ''
                                }))}
                                onChange={(e) => setMassiveClient(e)}
                            />
                        </Field>
                    </Step>
                    <Step title="3. Sube el archivo">
                        <Upload
                            accept=".xls,.xlsx"
                            onChange={({ file, fileList }) => {
                                LoadExcelData(file);
                            }}>
                            <Button disabled={disabled} icon={<UploadIcon />}>
                                Carga Masiva
                            </Button>
                        </Upload>
                    </Step>
                </div>
            )
        }
    ];

    const StoreColumns = [
        {
            title: 'Nombre',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <span className="text-xs text-surface-700">{text}</span>
        },
        {
            title: 'RIF',
            dataIndex: 'rif',
            key: 'rif',
            render: (text) => <span className="text-xs text-surface-500 tabular-nums">{text}</span>
        },
        {
            title: 'Cliente',
            dataIndex: 'clientName',
            key: 'clientName',
            render: (text) => <span className="text-xs text-surface-500">{text}</span>
        },
        {
            title: 'Tipo de PDV',
            dataIndex: 'storeType',
            key: 'storeType',
            render: (text) => <span className="text-xs text-surface-500">{text}</span>
        }
    ];

    return (
        <Drawer title={isEdit ? 'Editar Piso de Venta' : 'Crear Piso de Venta'} onClose={onCloseDrawer} open={open} width={700}>
            {contextHolder}
            <Tabs defaultActiveKey="1" items={tabs} />
            {stores?.length > 0 && (
                <div className="flex flex-col gap-4 mt-6 pt-6 border-t border-surface-100">
                    <p className="m-0 text-sm font-medium text-surface-700">4. Verifica la data cargada</p>
                    <div className="overflow-hidden rounded-lg border border-surface-200">
                        <Table size="small" dataSource={stores} columns={StoreColumns} pagination={{ pageSize: 20 }} className="[&_.ant-pagination]:px-4" />
                    </div>
                    <div className="flex justify-end">
                        <Button type="primary" disabled={disabled} onClick={() => createMultipleStores({ stores })}>
                            Cargar Pisos de Venta
                        </Button>
                    </div>
                </div>
            )}
        </Drawer>
    );
}
